import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Pencil, Trash2, Plus } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AffiliatesManager = () => {
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAffiliate, setEditingAffiliate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    established: '',
    members: '',
    contact: '',
    logo_url: '',
    website: '',
    order: 0
  });

  useEffect(() => {
    fetchAffiliates();
  }, []);

  const fetchAffiliates = async () => {
    try {
      const response = await axios.get(`${API}/affiliates`);
      setAffiliates(response.data);
    } catch (error) {
      console.error('Error fetching affiliates:', error);
      toast.error('Failed to load affiliates');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (affiliate = null) => {
    if (affiliate) {
      setEditingAffiliate(affiliate);
      setFormData({
        name: affiliate.name,
        description: affiliate.description,
        established: affiliate.established,
        members: affiliate.members,
        contact: affiliate.contact || '',
        logo_url: affiliate.logo_url || '',
        website: affiliate.website || '',
        order: affiliate.order
      });
    } else {
      setEditingAffiliate(null);
      setFormData({
        name: '',
        description: '',
        established: '',
        members: '',
        contact: '',
        logo_url: '',
        website: '',
        order: 0
      });
    }
    setDialogOpen(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'order' ? parseInt(value) : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem('admin_token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      if (editingAffiliate) {
        await axios.put(`${API}/affiliates/${editingAffiliate.id}`, formData, config);
        toast.success('Affiliate updated successfully');
      } else {
        await axios.post(`${API}/affiliates`, formData, config);
        toast.success('Affiliate created successfully');
      }

      setDialogOpen(false);
      fetchAffiliates();
    } catch (error) {
      console.error('Error saving affiliate:', error);
      toast.error('Failed to save affiliate');
    }
  };

  const handleDelete = async (affiliateId) => {
    if (!window.confirm('Are you sure you want to delete this affiliate?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/affiliates/${affiliateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Affiliate deleted successfully');
      fetchAffiliates();
    } catch (error) {
      console.error('Error deleting affiliate:', error);
      toast.error('Failed to delete affiliate');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Affiliates Management</h2>
          <p className="text-gray-600">Manage partner associations</p>
        </div>
        <Button
          onClick={() => handleOpenDialog()}
          data-testid="add-affiliate-btn"
          className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
        >
          <Plus className="mr-2" size={20} />
          Add Affiliate
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="spinner"></div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          {affiliates.map((affiliate) => (
            <div key={affiliate.id} data-testid={`affiliate-item-${affiliate.id}`} className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{affiliate.name}</h3>
                  <p className="text-sm text-gray-600 mb-3">{affiliate.description}</p>
                  <div className="flex gap-4 text-sm">
                    <span className="text-gray-600"><strong>Est:</strong> {affiliate.established}</span>
                    <span className="text-gray-600"><strong>Members:</strong> {affiliate.members}</span>
                  </div>
                  {affiliate.contact && (
                    <p className="text-sm text-gray-500 mt-2">Contact: {affiliate.contact}</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleOpenDialog(affiliate)}
                    data-testid={`edit-affiliate-${affiliate.id}`}
                    className="text-blue-600 hover:text-blue-800 transition-colors"
                  >
                    <Pencil size={18} />
                  </button>
                  <button
                    onClick={() => handleDelete(affiliate.id)}
                    data-testid={`delete-affiliate-${affiliate.id}`}
                    className="text-red-600 hover:text-red-800 transition-colors"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingAffiliate ? 'Edit Affiliate' : 'Add Affiliate'}</DialogTitle>
            <DialogDescription>
              {editingAffiliate ? 'Update affiliate information' : 'Add a new partner association'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} data-testid="affiliate-form" className="space-y-4">
            <div>
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                name="name"
                data-testid="input-name"
                value={formData.name}
                onChange={handleChange}
                required
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                name="description"
                data-testid="input-description"
                value={formData.description}
                onChange={handleChange}
                required
                rows={3}
                className="mt-1"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="established">Established Year *</Label>
                <Input
                  id="established"
                  name="established"
                  data-testid="input-established"
                  value={formData.established}
                  onChange={handleChange}
                  required
                  className="mt-1"
                  placeholder="e.g., 2020"
                />
              </div>
              <div>
                <Label htmlFor="members">Member Count *</Label>
                <Input
                  id="members"
                  name="members"
                  data-testid="input-members"
                  value={formData.members}
                  onChange={handleChange}
                  required
                  className="mt-1"
                  placeholder="e.g., 500+"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="contact">Contact Email</Label>
              <Input
                id="contact"
                name="contact"
                type="email"
                data-testid="input-contact"
                value={formData.contact}
                onChange={handleChange}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="logo_url">Logo URL</Label>
              <Input
                id="logo_url"
                name="logo_url"
                data-testid="input-logo-url"
                value={formData.logo_url}
                onChange={handleChange}
                className="mt-1"
                placeholder="https://example.com/logo.png"
              />
            </div>

            <div>
              <Label htmlFor="website">Website URL</Label>
              <Input
                id="website"
                name="website"
                data-testid="input-website"
                value={formData.website}
                onChange={handleChange}
                className="mt-1"
                placeholder="https://affiliate-website.com"
              />
            </div>

            <div>
              <Label htmlFor="order">Display Order</Label>
              <Input
                id="order"
                name="order"
                type="number"
                data-testid="input-order"
                value={formData.order}
                onChange={handleChange}
                className="mt-1"
                placeholder="0"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="submit"
                data-testid="submit-affiliate-btn"
                className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
              >
                {editingAffiliate ? 'Update Affiliate' : 'Add Affiliate'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AffiliatesManager;
